import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ASSESS_STATUSES, ASSESS_STATUS_LABEL, OPP_LEVELS, OPP_LEVEL_LABEL, statusBadge, levelBadge, fmtDate } from "@/lib/adminMeta";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Search, Flame, ClipboardList, Users, UserCheck, Download } from "lucide-react";

const Stat = ({ icon: Icon, label, value, tone, testid }) => (
  <div className="bg-white rounded-2xl border border-border p-4 flex items-center gap-3" data-testid={testid}>
    <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${tone}`}><Icon className="w-5 h-5" /></div>
    <div>
      <div className="text-2xl font-bold text-navy leading-none">{value ?? "-"}</div>
      <div className="text-xs text-muted-foreground mt-1">{label}</div>
    </div>
  </div>
);

export default function AdminAssessments() {
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [counts, setCounts] = useState({});
  const [loading, setLoading] = useState(true);
  const [f, setF] = useState({ q: "", status: "all", level: "all", high_priority: false });

  const load = useCallback(async () => {
    setLoading(true);
    const params = {};
    if (f.q) params.q = f.q;
    if (f.status && f.status !== "all") params.status = f.status;
    if (f.level && f.level !== "all") params.level = f.level;
    if (f.high_priority) params.high_priority = true;
    try {
      const { data } = await api.get("/admin/assessments", { params });
      setRows(data.rows || []);
      setCounts(data.counts || {});
    } catch { setRows([]); } finally { setLoading(false); }
  }, [f]);

  useEffect(() => { const t = setTimeout(load, 300); return () => clearTimeout(t); }, [load]);

  const exportCsv = async () => {
    const params = {};
    if (f.q) params.q = f.q;
    if (f.status && f.status !== "all") params.status = f.status;
    if (f.level && f.level !== "all") params.level = f.level;
    if (f.high_priority) params.high_priority = true;
    try {
      const res = await api.get("/admin/assessments-export", { params, responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "text/csv" }));
      const link = document.createElement("a");
      link.href = url;
      link.download = `marine-ad-ai-prospects-${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Prospect list exported");
    } catch { toast.error("Could not export prospects"); }
  };

  return (
    <div className="space-y-6 fade-up" data-testid="admin-assessments">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-3xl font-bold text-navy">Assessments</h1>
          <p className="text-sm text-muted-foreground mt-1">Prospect and marketing analytics from the public 60-Second Assessment. Read-only.</p>
        </div>
        <Button onClick={exportCsv} variant="outline" className="font-semibold" data-testid="assess-export-csv">
          <Download className="w-4 h-4 mr-1.5" /> Export Prospects (CSV)
        </Button>
      </div>

      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <Stat icon={ClipboardList} label="Total Completed" value={counts.total} tone="bg-navy/5 text-navy" testid="assess-stat-total" />
        <Stat icon={Users} label="Anonymous" value={counts.anonymous} tone="bg-slate-100 text-slate-600" testid="assess-stat-anon" />
        <Stat icon={UserCheck} label="Identified Prospects" value={counts.identified} tone="bg-blue-50 text-blue-700" testid="assess-stat-identified" />
        <Stat icon={Flame} label="High Priority" value={counts.high_priority} tone="bg-red-50 text-red-600" testid="assess-stat-priority" />
      </div>

      <div className="bg-white rounded-2xl border border-border p-4 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <div className="relative">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input value={f.q} onChange={(e) => setF((s) => ({ ...s, q: e.target.value }))} placeholder="Search identified prospects" className="pl-9" data-testid="assess-search" />
        </div>
        <Select value={f.status} onValueChange={(v) => setF((s) => ({ ...s, status: v }))}>
          <SelectTrigger data-testid="assess-filter-status"><SelectValue placeholder="Status" /></SelectTrigger>
          <SelectContent className="bg-white">
            <SelectItem value="all">All Statuses</SelectItem>
            {ASSESS_STATUSES.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={f.level} onValueChange={(v) => setF((s) => ({ ...s, level: v }))}>
          <SelectTrigger data-testid="assess-filter-level"><SelectValue placeholder="Opportunity Level" /></SelectTrigger>
          <SelectContent className="bg-white">
            <SelectItem value="all">All Opportunity Levels</SelectItem>
            {OPP_LEVELS.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}
          </SelectContent>
        </Select>
        <button
          onClick={() => setF((s) => ({ ...s, high_priority: !s.high_priority }))}
          data-testid="assess-filter-priority"
          className={`flex items-center justify-center gap-2 rounded-lg text-sm font-semibold border transition-colors ${f.high_priority ? "bg-red-600 text-white border-red-600" : "bg-white text-navy border-border hover:bg-secondary/50"}`}>
          <Flame className="w-4 h-4" /> High Priority Only
        </button>
      </div>

      <div className="bg-white rounded-2xl border border-border overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-secondary/60 text-navy">
              <tr className="text-left">
                {["Date", "Prospect", "Dealership", "Opportunity Level", "Status", "Inventory", "Top Opportunities"].map((h) => (
                  <th key={h} className="px-4 py-3 font-semibold whitespace-nowrap">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-border" data-testid="assess-table-body">
              {loading ? (
                <tr><td colSpan={7} className="px-4 py-10 text-center text-muted-foreground">Loading...</td></tr>
              ) : rows.length === 0 ? (
                <tr><td colSpan={7} className="px-4 py-10 text-center text-muted-foreground">No assessments match your filters.</td></tr>
              ) : rows.map((r) => (
                <tr key={r.id} onClick={() => navigate(`/admin/assessments/${r.id}`)} className="hover:bg-secondary/40 cursor-pointer" data-testid={`assess-row-${r.id}`}>
                  <td className="px-4 py-3 whitespace-nowrap">{fmtDate(r.created_at)}</td>
                  <td className="px-4 py-3 font-medium text-navy whitespace-nowrap">
                    <div className="flex items-center gap-2">
                      {r.identified ? (r.contact?.name || r.contact?.email || "Identified") : <span className="text-muted-foreground italic">Anonymous</span>}
                      {r.high_priority && <Flame className="w-3.5 h-3.5 text-red-600" data-testid={`assess-priority-${r.id}`} />}
                    </div>
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap">{r.identified ? (r.contact?.dealership_name || "-") : "-"}</td>
                  <td className="px-4 py-3"><span className={`text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded-full border ${levelBadge(r.top_level)}`}>{OPP_LEVEL_LABEL[r.top_level] || r.top_level_label}</span></td>
                  <td className="px-4 py-3"><span className={`text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded-full border ${statusBadge(r.status)}`}>{ASSESS_STATUS_LABEL[r.status] || r.status}</span></td>
                  <td className="px-4 py-3 whitespace-nowrap">{r.inventory || "-"}</td>
                  <td className="px-4 py-3 text-xs text-muted-foreground max-w-[220px]">{(r.top_opportunities || []).join(", ") || "-"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
